import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
    children: ReactNode;
    pageName?: string;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

/**
 * Per-page error boundary that captures render errors and provides
 * a graceful fallback UI with page context for debugging.
 */
export class PageErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error(`[PageError] ${this.props.pageName || 'Unknown'}:`, error, errorInfo);

        // Report to Sentry if available
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const w = window as any;
        if (typeof window !== 'undefined' && w.Sentry) {
            w.Sentry.captureException(error, {
                extra: {
                    pageName: this.props.pageName,
                    componentStack: errorInfo.componentStack,
                },
            });
        }
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null });
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-[60vh] flex items-center justify-center p-8">
                    <div className="text-center max-w-md space-y-4">
                        <div className="mx-auto w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center">
                            <AlertTriangle className="w-6 h-6 text-amber-600" />
                        </div>
                        <h2 className="text-xl font-semibold text-slate-900">
                            Something went wrong
                        </h2>
                        <p className="text-slate-500 text-sm">
                            {this.props.pageName
                                ? `An error occurred on the ${this.props.pageName} page.`
                                : 'An unexpected error occurred.'}
                            {' '}Your data is safe.
                        </p>
                        <div className="flex gap-3 justify-center pt-2">
                            <Button variant="outline" onClick={() => window.location.reload()}>
                                <RefreshCw className="w-4 h-4 mr-2" /> Reload Page
                            </Button>
                            <Button onClick={this.handleRetry}>
                                Try Again
                            </Button>
                        </div>
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <pre className="mt-4 p-3 bg-slate-50 rounded text-xs text-left text-red-600 overflow-auto max-h-40">
                                {this.state.error.message}
                            </pre>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
